import { z } from "zod";

/**
 * Catalog draft document: run-scoped overrides merged over the canonical catalog by
 * the backend resolver. Keys are constrained to the EXISTING catalog — a
 * draft can reword, reweight, replace a metric's check set, or patch
 * Content Fitness subcriterion text, but it cannot mint metric or
 * subcriterion keys (issues/history FK to `public.metrics` and
 * `content_fitness_subcriteria`). New metrics graduate via the code catalog.
 */

export const DraftCheckSchema = z.object({
  checkKey: z.string().min(1),
  points: z.number().int().min(1),
  question: z.string().min(1),
  evidenceHint: z.string(),
});
export type DraftCheck = z.infer<typeof DraftCheckSchema>;

/** Text-only patch for one existing subcriterion; keys stay canonical. */
export const DraftSubcriterionPatchSchema = z.object({
  definition: z.string().min(1).optional(),
  evidenceGuidance: z.string().optional(),
  applicabilityNotes: z.string().optional(),
  anchors: z.record(z.string(), z.string()).nullable().optional(),
  objectiveChecks: z.array(z.string()).optional(),
});
export type DraftSubcriterionPatch = z.infer<
  typeof DraftSubcriterionPatchSchema
>;

export const DraftMetricOverrideSchema = z.object({
  description: z.string().min(1).optional(),
  thresholds: z
    .object({
      target: z.number().int().optional(),
      good: z.number().int().optional(),
      watch: z.number().int().optional(),
    })
    .optional(),
  // Full replacement of the metric's check set (order = served position).
  // An empty array removes the metric from checklist scoring and coverage.
  checks: z.array(DraftCheckSchema).optional(),
  // Patches keyed by existing subcriterion key.
  subcriteria: z.record(z.string(), DraftSubcriterionPatchSchema).optional(),
});
export type DraftMetricOverride = z.infer<typeof DraftMetricOverrideSchema>;

export const CatalogDraftDocumentSchema = z.object({
  // Keyed by existing catalog metric key; the resolver fails fast on unknowns.
  metrics: z.record(z.string(), DraftMetricOverrideSchema),
});
export type CatalogDraftDocument = z.infer<typeof CatalogDraftDocumentSchema>;

export const CatalogDraftSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  parentDraftId: z.uuid().nullable(),
  document: CatalogDraftDocumentSchema,
  createdAt: z.coerce.date(),
  // A draft referenced by any run is frozen (copy-on-write): edits create a
  // new version so run history never lies about what it ran with.
  referenced: z.boolean(),
});
export type CatalogDraft = z.infer<typeof CatalogDraftSchema>;

export const CatalogDraftListResponseSchema = z.object({
  drafts: z.array(CatalogDraftSchema),
});
export type CatalogDraftListResponse = z.infer<
  typeof CatalogDraftListResponseSchema
>;

export const CatalogDraftWriteRequestSchema = z.object({
  label: z.string().min(1),
  document: CatalogDraftDocumentSchema,
});
export type CatalogDraftWriteRequest = z.infer<
  typeof CatalogDraftWriteRequestSchema
>;

/** Canonical catalog in editor shape: what a new draft diffs against. */
export const PlaygroundCatalogMetricSchema = z.object({
  key: z.string(),
  label: z.string(),
  pillar: z.string(),
  description: z.string(),
  thresholds: z.object({
    target: z.number().int(),
    good: z.number().int(),
    watch: z.number().int(),
  }),
  checks: z.array(DraftCheckSchema),
  subcriteria: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      pilot: z.boolean(),
      definition: z.string(),
      evidenceGuidance: z.string(),
      applicabilityNotes: z.string(),
      anchors: z.record(z.string(), z.string()).nullable(),
      objectiveChecks: z.array(z.string()),
    }),
  ),
});
export type PlaygroundCatalogMetric = z.infer<
  typeof PlaygroundCatalogMetricSchema
>;

export const PlaygroundCatalogResponseSchema = z.object({
  metrics: z.array(PlaygroundCatalogMetricSchema),
});
export type PlaygroundCatalogResponse = z.infer<
  typeof PlaygroundCatalogResponseSchema
>;
